import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';


export default class Wifi extends BaseFeature {
    constructor() {
        super();
        this.name = 'Wi-Fi';
        this.id = 'wifi';
        this.icon = 'FaWifi';
        this.priority = 99;
        this.requirements = ['raspberrypi_rpi_pico_rp2040_w'];
        this.capabilities = [
            'network',
        ];
        const settings = [
            {
                id: 'ENABLED',
                name: 'Wi-Fi Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'MODE',
                name: 'Mode',
                settingType: SettingType.OPTION,
                required: true,
                // readonly: true,
                default: 'station',
                options: [
                    {
                        name: 'Station',
                        value: 'station',
                    },
                    {
                        name: 'AP',
                        value: 'ap',
                    },
                ],
            },
            {
                id: 'DHCP',
                name: 'DHCP',
                settingType: SettingType.OPTION,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFCP',
                    NAME: 'Wifi DHCP',
                    SETTINGTYPE: 'float',
                    DATATYPE: 'B',
                    MIN: 0,
                    MAX: 1,
                    DEFAULT: '1',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'Disabled',
                            value: '0',
                        },
                        {
                            name: 'Enabled',
                            value: '1',
                        },
                    ],
                },
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'IP',
                name: 'IP Address',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFIP',
                    NAME: 'Wifi IP',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
            {
                id: 'NETMASK',
                name: 'Netmask',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFNM',
                    NAME: 'Wifi Netmask',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '255.255.255.0',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '255.255.255.0',
            },
            {
                id: 'GATEWAY',
                name: 'Gateway',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFGW',
                    NAME: 'Wifi Gateway',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
            {
                id: 'WAP',
                name: 'Access Point SSID',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFAP',
                    NAME: 'Wifi AP SSID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 32,
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: '',
            },
            {
                id: 'WPW',
                name: 'Password',
                settingType: SettingType.PASSWORD,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFPW',
                    NAME: 'Wifi Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 64,
                },
                UIControl: {
                    CONTROL: 'PASSWORD',
                },
                required: true,
                default: '',
            },
        ];


        this.settings = this.settings.concat(settings);
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const { project } = this;

        return errors;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.DISABLED;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            case 'CHANNEL':
                if (this.getSettingValue('MODE') === 'ap') {
                    return SettingState.ENABLED;
                }
                return SettingState.HIDDEN;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.getSettingValue('ENABLED').toString() === '0') {
            return blocks;
        }

        const { project } = this;
        const SSID = this.getSettingValue('WAP');
        const SECURITY = this.getSettingValue('WSEC');
        const PASSWORD = this.getSettingValue('WPW');
        const DHCP = this.getSettingValue('DHCP');
        const IP = this.getSettingValue('IP');
        const NETMASK = this.getSettingValue('NETMASK');
        const GATEWAY = this.getSettingValue('GATEWAY');
        let DHCPIsLinked = false;
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === this.getSettingValue('ID')
                    && feature.getSettingValue('SETTINGID') === 'DHCP') {
                    DHCPIsLinked = true;
                }
            }
        }

        blocks.push({
            location: 'IMPORTS',
            blockType: BlockType.CODE,
            template: `
import network`,
        });

        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
WLN_SSID = ${SSID}
WLN_PWD = ${PASSWORD}`,
        });

        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
wlan = network.WLAN(network.STA_IF)`,
        });

        const ipSetting = project.getLinkedSetting(this.getID(), 'IP');
        let ipSettingCode = '';
        if (ipSetting !== undefined) {
            const settingItem = project.getFeature(ipSetting.getSettingValue('LINKEDSETTING'));
            ipSettingCode = `var_${settingItem.getSettingValue('SETTINGID')}_set(wlan.ifconfig()[0])`;
        }

        blocks.push({
            location: 'FUNCTION_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
def wln_connect():
    wlan.active(True) # power up the WiFi chip
    print('Waiting for wifi chip to power up...')
    time.sleep(1.5) # wait three seconds for the chip to power up and initialize
    if ${DHCP}:
        wlan.ipconfig(dhcp4=True)
    else:
        wlan.ipconfig(addr4=(${IP}, ${NETMASK}), gw4=${GATEWAY})
    wlan.connect(WLN_SSID, WLN_PWD)
    print(f'Connecting to wifi {WLN_SSID}...')
    max_wait = 10
    while max_wait > 0:
        if wlan.isconnected():
            break
        max_wait -= 1
        print(f'wifi attempt {max_wait}')
        time.sleep(1)

    # Handle connection error
    if wlan.isconnected() != True:
        print(f'Wifi connection failed')
        # raise RuntimeError('network connection failed')
    else:
        print('connected')
        status = wlan.ifconfig()
        print( 'ip = ' + status[0] )
    
    if wlan.isconnected():
        print('Success! We have connected to your access point!')
        print(f'Try to ping the device at {wlan.ifconfig()[0]}')
        ${ipSettingCode}
        #2=wifi, 1=ethernet
        interface_set(2,1)
        #****services_start****#
    else:
        print('Failure! We have not connected to your access point!  Check your secrets.py file for errors.')`,
        });

        blocks.push({
            location: 'boot_complete',
            blockType: BlockType.CODE,
            template: `
wln_connect()`,
        });

        return blocks;
    }
}
